'use client'

/**
 * src/components/LeaderboardPrefs.tsx
 *
 * Controls for the user's public-leaderboard display:
 *   - Pseudonym (text input). Empty string clears it (reverts to the
 *     deterministic default).
 *   - Show-on-leaderboard toggle. Hard global opt-out across both the
 *     public board and any group boards.
 *
 * The component lazy-loads the current values via
 * GET /api/me/leaderboard-prefs and persists changes via POST. The
 * server is the source of truth; we don't trust the initial-render
 * values for anything privacy-sensitive.
 */

import { useState } from 'react'

type Prefs = {
  pseudonym: string | null
  showOnLeaderboard: boolean
  displayName: string
}

// `initialPrefs` is resolved server-side by the settings page (#745) — the
// server is still the source of truth for the privacy-sensitive values; we just
// no longer round-trip /api/me/leaderboard-prefs on mount to get them.
export default function LeaderboardPrefs({ initialPrefs }: { initialPrefs: Prefs }) {
  const [pseudonym, setPseudonym] = useState(initialPrefs.pseudonym ?? '')
  const [displayName, setDisplayName] = useState(initialPrefs.displayName)
  const [showOn, setShowOn] = useState(initialPrefs.showOnLeaderboard)

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedTick, setSavedTick] = useState(false)

  async function save(updates: Partial<Prefs>) {
    setSaving(true)
    setError(null)
    try {
      const res = await fetch('/api/me/leaderboard-prefs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        cache: 'no-store',
        body: JSON.stringify(updates),
      })
      const data = await res.json()
      if (!res.ok) {
        setError(typeof data?.error === 'string' ? data.error : 'Save failed.')
        return
      }
      setPseudonym(data.pseudonym ?? '')
      setDisplayName(data.displayName)
      setShowOn(data.showOnLeaderboard)
      setSavedTick(true)
      setTimeout(() => setSavedTick(false), 1500)
    } catch {
      setError('Network error. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  function onSavePseudonym() {
    const trimmed = pseudonym.trim()
    save({ pseudonym: trimmed === '' ? null : trimmed })
  }

  function onToggleShow(next: boolean) {
    setShowOn(next) // optimistic
    save({ showOnLeaderboard: next })
  }

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="pseudonym-input" className="text-sm font-medium text-foreground">
          Public name
        </label>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Shown next to your scores on the leaderboard. We never show your legal name. Leave empty
          to use the default we picked for you.
        </p>
        <div className="mt-2 flex gap-2">
          <input
            id="pseudonym-input"
            type="text"
            value={pseudonym}
            onChange={(e) => setPseudonym(e.target.value)}
            disabled={saving}
            placeholder={displayName}
            maxLength={20}
            className="flex-1 rounded-md border border-border bg-background px-3 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
          />
          <button
            type="button"
            onClick={onSavePseudonym}
            disabled={saving}
            className="rounded-md border border-brand/40 bg-brand/10 px-3 py-1.5 text-sm font-medium text-brand-ink transition-colors hover:bg-brand/20 disabled:opacity-60 dark:text-brand-ink"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Currently shown as <span className="font-medium">{displayName}</span>
          {savedTick && <span className="ml-2 text-emerald-600 dark:text-emerald-400">Saved</span>}
        </p>
        {error && <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">{error}</p>}
      </div>

      <div className="flex items-start justify-between gap-4 border-t border-border pt-4">
        <div className="min-w-0 flex-1">
          <div id="leaderboard-show-label" className="text-sm font-medium text-foreground">
            Show me on the leaderboard
          </div>
          <p id="leaderboard-show-help" className="mt-0.5 text-sm text-muted-foreground">
            When off, you&apos;re hidden from the public leaderboard and from any study-group
            leaderboards. You can still see your own scores in your dashboard.
          </p>
        </div>
        <label className="relative inline-flex cursor-pointer items-center">
          <input
            type="checkbox"
            aria-labelledby="leaderboard-show-label"
            aria-describedby="leaderboard-show-help"
            checked={showOn}
            disabled={saving}
            onChange={(e) => onToggleShow(e.target.checked)}
            className="peer sr-only"
          />
          <span className="block h-6 w-11 rounded-full bg-muted transition-colors peer-checked:bg-brand peer-disabled:opacity-50" />
          <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5" />
        </label>
      </div>
    </div>
  )
}
